import { debounce } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../../../styles/theme';
import { Typography } from '../../../../style/Typography';

type MinMaxNumericInputFieldProps = {
  value: number;
  minPossibleValue?: number;
  maxPossibleValue?: number;
  handleInput: (value: number) => void;
  label: string;
  debounceTime?: number;
  fontColor?: string;
  isPricePrefix?: boolean;
};

const NumericInputField: FC<MinMaxNumericInputFieldProps> = (props) => {
  const [inputValue, setInputValue] = useState<string>(
    props.value === -1 ? '' : props.value.toString(),
  );

  const debouncedHandleInput = useCallback(
    debounce((value: number) => {
      props.handleInput(value);
    }, props.debounceTime ?? 500),
    [props.handleInput],
  );

  const validate = useCallback(
    (value: string) => {
      const parsedValue = parseInt(value);
      if (
        (props.minPossibleValue !== undefined && parsedValue < props.minPossibleValue) ||
        (props.maxPossibleValue !== undefined && parsedValue > props.maxPossibleValue)
      ) {
        return;
      }
      setInputValue(value);
      debouncedHandleInput(parsedValue);
    },
    [props.minPossibleValue, props.maxPossibleValue, debouncedHandleInput],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    validate(newValue);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === '+' || event.key === '-' || event.key === '.') {
      event.preventDefault();
    }
  };

  useEffect(() => {
    setInputValue(props.value === -1 ? '' : props.value.toString());
  }, [props.value]);

  return (
    <div>
      <label className='form-label'>
        <Typography
          size={theme.typography.h5}
          fontWeight={theme.typography.fontWeights.semibold}
          color={props?.fontColor}
        >
          {props.label}
        </Typography>
      </label>
      <InputContainer isPricePrefix>
        {props.isPricePrefix && <PriceTag>$</PriceTag>}
        <input
          data-testid='numeric-input-field'
          type='number'
          className='form-control'
          min={props.minPossibleValue}
          max={props.maxPossibleValue}
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
        />
      </InputContainer>
    </div>
  );
};

const InputContainer = styled.div<{ isPricePrefix?: boolean }>`
  display: flex;
  align-items: flex-end;

  > input {
    height: ${(props) => (props.isPricePrefix ? 'fit-content' : 'auto')};
  }
`;

const PriceTag = styled.span`
  color: white;
  font-size: 15px;
  font-weight: 500;
`;

export default NumericInputField;
